import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { API_BASE_URL } from '../api-config';
import { formatPriceMx } from './catalog-helpers';
import type { ApiProduct, ApiProductPage } from './catalog-api.models';
import type { CatalogProduct } from './catalog.models';
function toSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

const CATEGORY_SLUG_MAP: Record<string, CatalogProduct['category']> = {
  recamaras: 'recamaras',
  muebles: 'muebles'
};

function mapProduct(p: ApiProduct): CatalogProduct {
  const priceValue = parseFloat(p.price) || 0;
  const sortedImages = [...(p.product_images ?? [])].sort((a, b) => {
    if (a.is_primary !== b.is_primary) return a.is_primary ? -1 : 1;
    return a.sort_order - b.sort_order;
  });
  const image = sortedImages[0]?.image_url ?? '';
  const gallery = sortedImages.map((i) => i.image_url).filter(Boolean);
  const category: CatalogProduct['category'] =
    CATEGORY_SLUG_MAP[p.category?.slug ?? ''] ?? 'muebles';

  return {
    id: String(p.id),
    slug: p.sku ? toSlug(p.sku) : toSlug(p.product_name),
    name: p.product_name,
    price: formatPriceMx(priceValue),
    priceValue,
    image,
    gallery: gallery?.length ? gallery : undefined,
    category,
    sku: p.sku || undefined,
    lead: p.description || undefined,
    rating: p.rating ?? undefined,
    reviewCount: p.review_count ?? undefined,
    benefits: p.benefits?.length ? p.benefits : undefined
  };
}

@Injectable({ providedIn: 'root' })
export class ProductCatalogService {
  private readonly http = inject(HttpClient);
  private readonly base = inject(API_BASE_URL);

  private readonly _products = signal<CatalogProduct[]>([]);

  readonly products = this._products.asReadonly();

  readonly featuredProducts = computed(() => this._products().slice(0, 6));

  loadPage(page = 1, elements = 100): void {
    this.http
      .get<ApiProductPage>(`${this.base}/products`, {
        params: { page: String(page), elements: String(elements) }
      })
      .subscribe({
        next: (res) => this._products.set(res.data.map(mapProduct)),
        error: (err) => console.error('Error cargando productos', err)
      });
  }

  getBySlug(slug: string | null): CatalogProduct | undefined {
    if (!slug) return undefined;
    return this._products().find((p) => p.slug === slug);
  }

  getById(id: string): CatalogProduct | undefined {
    return this._products().find((p) => p.id === id);
  }

  slugExists(slug: string, exceptId?: string): boolean {
    return this._products().some((p) => p.slug === slug && p.id !== exceptId);
  }

  upsert(product: CatalogProduct): void {
    const normalized: CatalogProduct = {
      ...product,
      price: formatPriceMx(product.priceValue),
      oldPrice: product.oldPrice?.trim() ? product.oldPrice.trim() : undefined
    };
    this._products.update((list) => {
      const i = list.findIndex((p) => p.id === normalized.id);
      if (i === -1) return [...list, normalized];
      const next = [...list];
      next[i] = normalized;
      return next;
    });
  }

  delete(id: string): void {
    this._products.update((list) => list.filter((p) => p.id !== id));
  }

  nextId(): string {
    const max = this._products().reduce((m, p) => Math.max(m, parseInt(p.id, 10) || 0), 0);
    return String(max + 1);
  }

  /** Guarda (crea o actualiza) un producto en el API y actualiza la señal local. */
  saveProduct(
    product: CatalogProduct,
    categoryId: number,
    isNew: boolean
  ): Observable<ApiProduct> {
    const allImages = [
      ...(product.image ? [{ url: product.image, is_primary: true, sort_order: 0 }] : []),
      ...(product.gallery ?? []).map((url, i) => ({ url, is_primary: false, sort_order: i + 1 }))
    ];

    const payload = {
      product_name: product.name,
      sku: product.sku ?? product.slug,
      description: product.lead ?? null,
      price: String(product.priceValue),
      stock: 0,
      category_id: categoryId,
      is_active: true,
      rating: product.rating ?? null,
      review_count: product.reviewCount ?? null,
      benefits: product.benefits ?? null,
      images: allImages
    };

    const req$ = isNew
      ? this.http.post<ApiProduct>(`${this.base}/products`, payload)
      : this.http.patch<ApiProduct>(`${this.base}/products/${product.id}`, payload);

    return req$.pipe(tap((saved) => this.upsert(mapProduct(saved))));
  }

  /** Elimina un producto en el API y lo quita de la señal local. */
  deleteProduct(id: string): Observable<unknown> {
    return this.http
      .delete(`${this.base}/products/${id}`)
      .pipe(tap(() => this.delete(id)));
  }
}
